import React, { useCallback, useEffect, useState } from "react";
import { IFolderModel, rootFolder } from "src/Core/ViewModels/Folder/FolderModel";
import MessageExplorer from "./MessageExplorer";
import { Option } from "react-dropdown";
import { ArchiveFolderType, FolderColumnName, FolderType, SortOrder, TabType } from "src/Core/Common/Enums";
import { getDaysFromRetentionPeriodId, getRetentionOptions } from "src/helper/HelperFunctions";
import { Col, FormControl, OverlayTrigger, Popover, PopoverBody, Row } from "react-bootstrap";
import FolderActionsPopUp from "../Message/Common/FolderActionPopUp";
import ArchiveMessage, { IArchiveMessageProps } from "./ArchiveMessage";
import FolderBreadCrumb from "../Common/Navigation/FolderBreadCrumb";
import { useDispatch, useSelector } from "react-redux";
import {
    fetchFolderDetailsWithPermission,
    fetchRetentionPeriods,
    fetchSubFolders,
    fetchUserOwnedFolders,
    isFolderExist,
} from "src/actionCreators/folderActionCreators";
import { ApplicationState } from "src/store";
import { logger } from "src/routes/LoggedIn";
import { appInsightsData } from "src/helper/AppInsightsData";
import { FolderTypeIcon } from "src/assets/images/svg/FolderIcons";
import { ArchiveConstants } from "src/Core/Common/Constants";
import { RefreshIcon, SearchIcon } from "src/assets/images/svg/IconCollection";
import { debounce } from "throttle-debounce";
import { currentFolderIdChanged, currentPageChanged } from "src/actionCreators/signalRActionCreators";
import { AppNotifier } from "../Common/Notification/AppNotifier";
import { ToasterMessages } from "src/helper/ToasterMessages";
import { ITabModel } from "src/Core/ViewModels/settings/Setting";
import Tab from "../AdminSettings/parts/Tab";
import { fetchFolderNotificationsSettings, fetchRetentionSettings } from "src/actionCreators/adminSettingsActionCreators";
import { IRetentionPeriod } from "src/Core/ViewModels/Company/Settings/RetentionSettings";
import { fetchUserSettings } from "src/actionCreators/userSettingsActionCreators";

export const ArchiveContext = React.createContext("");

const Archive: React.FC = () => {
    const [breadcrumbArray, setBreadcrumbArray] = useState<IFolderModel[]>([rootFolder]);
    const [showArchiveMessages, toggleArchiveMessages] = useState<boolean>(false);
    const [showMessageLoader, toggleMessageLoader] = useState<boolean>(false);
    const [selectedFolderId, setSelectedFolderId] = useState<number>(0);
    const [showFolderActionPopUp, setShowFolderActionPopUp] = useState(false);
    const [retentionPeriods, setRetentionPeriods] = useState<Option[]>([]);
    const [reload, setReload] = useState(false);
    const [selectedFolders, setSelectedFolders] = useState<IFolderModel[]>([]);
    const [searchFolderText, setSearchFolderText] = useState<string>("");
    const [folderName, setFolderName] = useState<string>(breadcrumbArray[breadcrumbArray.length - 1].folderName);
    const currentFolderPath = breadcrumbArray.map(folder => folder.folderName).join("/");
    const currentFolder: IFolderModel = breadcrumbArray[breadcrumbArray.length - 1];
    const parentFolder: IFolderModel =
        breadcrumbArray.length > 1
            ? breadcrumbArray[breadcrumbArray.length - 2]
            : breadcrumbArray[breadcrumbArray.length - 1];

    const [tabList, setTabList] = useState<ITabModel[]>([]);
    const [activeTab, setActiveTab] = useState<TabType>(TabType.ArchiveFiles);

    const [folderRetentions, setFolderRetentions] = useState<IRetentionPeriod[]>([]);

    const dispatch = useDispatch();
    const { messageOptions: messageOptionsSettings } = useSelector(
        (appState: ApplicationState) => appState.adminSettingsState
    );
    const { userSettings } = useSelector((appState: ApplicationState) => appState.userSettingsState);
    const retentionData = useSelector((appState: ApplicationState) => appState.folderState.retentionPeriodsData);
    const { mailboxUserData } = useSelector((appState: ApplicationState) => appState.commonState);
    const folderData = useSelector((appState: ApplicationState) => appState.folderState.foldersData.subFolders);

    const signalRState = useSelector((appState: ApplicationState) => appState.signalRState);

    const commonDependencies = [retentionData.retentionPeriods, userSettings?.retentionSettings?.defaultRetentionPeriods, messageOptionsSettings.retentionPeriodList.length, messageOptionsSettings.retentionSettings?.defaultRetentionPeriods?.folders];

    useEffect(() => {
        dispatch(currentPageChanged(FolderType.ARCHIVE));
        dispatch(fetchUserOwnedFolders());
        dispatch(currentFolderIdChanged(currentFolder.folderId));
        dispatch(fetchRetentionPeriods());
        dispatch(fetchRetentionSettings());
        dispatch(fetchUserSettings());
        dispatch(fetchFolderNotificationsSettings());
        dispatch(fetchSubFolders(currentFolder.folderId, FolderColumnName.FOLDERNAME, SortOrder.ASC));
        // Page track view call
        logger.trackPageView(appInsightsData.Folders.MyFiles.PageTitle);
        // clean up code goes here
        return () => {
            goToFolder(0, 0);
        };
    }, [mailboxUserData]);

    useEffect(() => {
        let folderRetention = retentionData.retentionPeriods;
        let maxRetention = 0;
        if (messageOptionsSettings.retentionSettings?.isUserAllowedToChangeFolderRetentionPeriod) {
            if (currentFolder.folderId === 0) {
                // if user default retention period for folder is not set initially, then use default retention period for folder from admin settings
                maxRetention = getDaysFromRetentionPeriodId(userSettings?.retentionSettings.defaultRetentionPeriods.folders
                    || messageOptionsSettings.retentionSettings.defaultRetentionPeriods.folders, retentionData.retentionPeriods);
            } else {
                maxRetention = currentFolder.expiresDays;
            }
            folderRetention = retentionData.retentionPeriods.filter(rp => rp.days <= maxRetention);
        }
        setFolderRetentions(folderRetention);
    }, [reload, currentFolder, ...commonDependencies]);

   useEffect(() => {
        setActiveTab(activeTab || TabType.ArchiveFiles);
        const tabList: ITabModel[] = [
            {
                title: "Files",
                type: TabType.ArchiveFiles,
                isActive: activeTab === TabType.ArchiveFiles,
            },
            {
                title: "Message",
                type: TabType.ArchiveMessage,
                isActive: activeTab === TabType.ArchiveMessage,
            },
            {
                title: "Request",
                type: TabType.ArchiveRequest,
                isActive: activeTab === TabType.ArchiveRequest,
            },
        ];
        setTabList(tabList);
    }, [currentFolder]);

    useEffect(() => {
        if (!folderData.isLoading) {
            if (folderData.folders?.length > 0) {
                setBreadcrumbArray(arrayValue =>
                    arrayValue.map(folder =>
                        folder.folderId === currentFolder.folderId
                            ? { ...folder, folderType: ArchiveFolderType.FOLDER }
                            : folder
                    )
                );
            }
            if (folderData.folders?.length === 0) {
                setBreadcrumbArray(arrayValue =>
                    arrayValue.map(folder =>
                        folder.folderId === currentFolder.folderId
                            ? { ...folder, folderType: ArchiveFolderType.UNKNOWN }
                            : folder
                    )
                );
            }
        }
    }, [folderData]);

    useEffect(() => {
        setBreadcrumbArray(
            breadcrumbArray.map(data => (data.folderId == currentFolder.folderId ? { ...data, folderName } : data))
        );
    }, [folderName]);

    useEffect(() => {
        dispatch(fetchRetentionSettings());
        dispatch(fetchUserSettings());
        dispatch(fetchRetentionPeriods());
        setSearchFolderText("");
        dispatch(
            isFolderExist(currentFolder.folderId, (result: boolean) => {
                if (!result) {
                    AppNotifier.Warning(ToasterMessages.WARNING.CHANGE_FOLDER_PERMISSION);
                    goToFolder(0, 0);
                } else {
                    dispatch(
                        fetchSubFolders(currentFolder.folderId, FolderColumnName.FOLDERNAME, SortOrder.ASC, () => {
                            setSelectedFolders([]);
                            dispatch(fetchUserOwnedFolders());
                        })
                    );
                    if (currentFolder.folderId > 0) {
                        dispatch(
                            fetchFolderDetailsWithPermission(currentFolder.folderId, result => {
                                setFolderName(result.folderName);
                                result.folderType === ArchiveFolderType.MESSAGE && toggleArchiveMessages(true);
                                setSelectedFolderId(result.folderId);
                                setBreadcrumbArray(
                                    breadcrumbArray.map(data =>
                                        data.folderId == currentFolder.folderId
                                            ? {
                                                ...data,
                                                folderName: result.folderName,
                                                permissionGroupId: result.permissionGroupId,
                                                expiresDays: result.expiresDays,
                                                description: result.description,
                                                folderType: result.folderType,
                                            }
                                            : data
                                    )
                                );
                            })
                        );
                    }
                    setSelectedFolderId(currentFolder.folderId);
                }
            })
        );
    }, [reload, signalRState.archiveFolderReload]);

    useEffect(() => {
        setShowFolderActionPopUp(false);
        dispatch(currentFolderIdChanged(currentFolder.folderId));
    }, [currentFolder.folderId]);

    useEffect(() => {
        let retentionDays;
        if (parentFolder.folderId === 0) {
            retentionDays = retentionData.retentionPeriods;
        } else {
            retentionDays = retentionData.retentionPeriods.filter(rp => rp.days <= parentFolder.expiresDays);
        }
        setRetentionPeriods(getRetentionOptions(retentionDays));
    }, [parentFolder, retentionData]);

    const getFolderData = (value: string) => {
        dispatch(
            fetchSubFolders(currentFolder.folderId, FolderColumnName.FOLDERNAME, SortOrder.ASC, undefined, value ?? "")
        );
    };

    const handleTabClick = (tabType: TabType) => {
        setTabList(
            tabList.map(tab => (tab.type === tabType ? { ...tab, isActive: true } : { ...tab, isActive: false }))
        );
        setActiveTab(tabType);
    };

    const debounceFolderSearchFn = useCallback(debounce(1000, getFolderData), [currentFolder]);

    const onFolderFilter: React.ChangeEventHandler<HTMLInputElement> = e => {
        const value = (e.target as HTMLInputElement).value.toLocaleLowerCase();
        setSearchFolderText(value);
        debounceFolderSearchFn(value);
        if (value == "") {
            setSelectedFolders([]);
        }
    };

    const goToFolder = (
        folderId: number,
        breadcrumbIndex: number,
        isTitleBreadcrumb = true,
        breadCrumbArrayArchiveTable: IFolderModel[] = []
    ) => {
        if (folderId !== rootFolder.folderId && folderId == currentFolder.folderId) return;

        // Reset search text
        setSearchFolderText("");

        //fetch sub folders;
        isTitleBreadcrumb
            ? setBreadcrumbArray(value => value.filter((folder, index) => index <= breadcrumbIndex))
            : setBreadcrumbArray(() => {
                breadcrumbArray.pop();
                return breadcrumbArray.concat(
                    breadCrumbArrayArchiveTable.filter((folder, index) => index <= breadcrumbIndex)
                );
            });
        dispatch(fetchSubFolders(folderId, FolderColumnName.FOLDERNAME, SortOrder.ASC));
        let showMessageView = false;
        if (!isTitleBreadcrumb) {
            const folderType = breadCrumbArrayArchiveTable[breadcrumbIndex].folderType;
            showMessageView = folderType === ArchiveFolderType.MESSAGE;
            setSelectedFolderId(folderId);
        }
        toggleArchiveMessages(showMessageView);
        setFolderName(
            isTitleBreadcrumb
                ? breadcrumbArray[breadcrumbIndex].folderName
                : breadCrumbArrayArchiveTable[breadcrumbIndex].folderName
        );
        setActiveTab(TabType.ArchiveFiles);
    };

    const handleFolderClick = (
        folder: IFolderModel,
        isTitleBreadcrumb = true,
        breadCrumbArrayArchiveTable: IFolderModel[] = []
    ) => {
        // Map the breadcrumb array to the folders api response
        const tempBreadcrumbArray = [];
        breadCrumbArrayArchiveTable.forEach((breadCrumbFolder, index) => {
            if (index === 0) {
                tempBreadcrumbArray.push(breadCrumbFolder);
                return;
            }
            const newFolder = folderData && folderData.folders.find(f => f.folderId === breadCrumbFolder.folderId);
            if (newFolder) {
                tempBreadcrumbArray.push(newFolder);
            }
        });

        // Reset folder search textbox
        setSearchFolderText("");
        isTitleBreadcrumb
            ? setBreadcrumbArray(value => [...value, folder])
            : setBreadcrumbArray(() => {
                breadcrumbArray.pop();
                return breadcrumbArray.concat(tempBreadcrumbArray);
            });
        setFolderName(
            isTitleBreadcrumb ? folder.folderName : tempBreadcrumbArray[tempBreadcrumbArray.length - 1].folderName
        );

        if (folder.folderType === ArchiveFolderType.MESSAGE) {
            toggleArchiveMessages(true);
            setSelectedFolderId(folder.folderId);
        } else {
            toggleMessageLoader(false);
            toggleArchiveMessages(false);
        }
    };

    const handlePopUpActionSubmission = (value: boolean, folder: IFolderModel, updatedNotification?:boolean) => {
        setShowFolderActionPopUp(false);
        if (folder) {
            const breadcrumbDataCopy = [...breadcrumbArray];
            const currentFolderIndex = breadcrumbDataCopy.findIndex(item => item.folderId === currentFolder.folderId);
            breadcrumbDataCopy[currentFolderIndex] = folder;
            setBreadcrumbArray(breadcrumbDataCopy);
        }
        if(updatedNotification!== undefined){ 
            const breadcrumbDataCopy = [...breadcrumbArray];
            const currentFolderIndex = breadcrumbDataCopy.findIndex(item => item.folderId === currentFolder.folderId);
            breadcrumbDataCopy[currentFolderIndex].isFolderNotificationActive = updatedNotification;
            setBreadcrumbArray(breadcrumbDataCopy);
        }
    };
    const goToParentFolder = (isInlineLevel: boolean) => {
        if (!isInlineLevel) {
            const parentIndex = breadcrumbArray.length > 1 ? breadcrumbArray.length - 2 : breadcrumbArray.length - 1;
            return goToFolder(parentFolder.folderId, parentIndex);
        }
    };

    const headerActionPopover = (
        <Popover
            style={{
                zIndex: 1000,
            }}
            className="folder-actions-popover"
        >
            <PopoverBody
                style={{
                    padding: 0,
                }}
            >
                <span>
                    <FolderActionsPopUp
                        onHide={() => {
                            setShowFolderActionPopUp(false);
                        }}
                        selectedFolders={[currentFolder]}
                        retentionOptions={retentionPeriods}
                        setReload={handlePopUpActionSubmission}
                        goToParentFolder={goToParentFolder}
                        setFolderName={setFolderName}
                        setShowFolderActionPopup={setShowFolderActionPopUp}
                        currentFolder={currentFolder}
                    />
                </span>
            </PopoverBody>
        </Popover>
    );

    const handleRefreshButtonClick = () => {
        setSearchFolderText("");
        setReload(value => !value);
        dispatch(fetchFolderNotificationsSettings());
    };

    const archiveProps: IArchiveMessageProps = {
        folderId: selectedFolderId,
        folder: currentFolder,
        reload: reload,
        handleFolderRefreshClick: handleRefreshButtonClick,
        isFolderRefresh: true,
        goToParentFolder: goToParentFolder
    };

    return (
        <div className="archive-container">
            <FolderBreadCrumb breadCrumbArray={breadcrumbArray} goToFolder={goToFolder} isTitleBreadcrumb={true} />

            <div className="main-folder-title-container">
                <FolderTypeIcon height={34} width={34} folderType={currentFolder.folderType} />
                <span title={folderName}>{folderName}</span>
                {currentFolder.folderId !== rootFolder.folderId && (
                    <OverlayTrigger
                        placement="auto-start"
                        overlay={headerActionPopover}
                        trigger="click"
                        rootClose
                        onToggle={(show: boolean) => setShowFolderActionPopUp(show)}
                        show={showFolderActionPopUp}
                    >
                        <i className="fas fa-ellipsis-h cursor-pointer" />
                    </OverlayTrigger>
                )}
            </div>

            {showArchiveMessages ? (

                <Tab tabs={tabList} handleTabClick={handleTabClick} activeTab={activeTab} archiveProps={archiveProps} archiveFolder={currentFolder.folderId} />
            ) : (
                <>
                    <Row className="search-header-wrapper folder-search-wrapper">
                        <Col className="search-input-wrapper-col col-10">
                            <div className="search-input-wrapper">
                                <FormControl
                                    placeholder={`${ArchiveConstants.PLACEHOLDER_SEARCH_TEXT} ${currentFolder.folderName}`}
                                    value={searchFolderText}
                                    onChange={onFolderFilter}
                                    className="search-input search-textbox"
                                />
                                <div className="search-icon">
                                    <SearchIcon />
                                </div>
                            </div>
                        </Col>
                        <Col className="refresh-button-container col-2">
                            <div
                                className="refresh-button-wrapper"
                                title={ArchiveConstants.REFRESH_TITLE}
                                onClick={handleRefreshButtonClick}
                            >
                                <RefreshIcon />
                            </div>
                        </Col>
                    </Row>
                    <div className="viewer-container margin-top-3">
                        <ArchiveContext.Provider value={currentFolderPath}>
                            <MessageExplorer
                                handleFolderClick={handleFolderClick}
                                currentFolder={currentFolder}
                                retentionPeriods={folderRetentions}
                                isRetentionLoading={retentionData.isLoading}
                                isMessageLoading={showMessageLoader}
                                setReload={setReload}
                                goToParentFolder={goToParentFolder}
                                onHide={() => {
                                    setShowFolderActionPopUp(false);
                                }}
                                isSearched={!!searchFolderText}
                                selectedFolders={selectedFolders}
                                setSelectedFolders={setSelectedFolders}
                                goToFolder={goToFolder}
                            />
                        </ArchiveContext.Provider>
                    </div>
                </>
            )}
        </div>
    );
};
Archive.displayName = Archive.name;
export default Archive;
